import React, { useState } from 'react';
// import _ from 'lodash';
import {
  AutoSizer,
  Column,
  SortDirectionType,
  Table,
  TableCellProps,
  TableHeaderProps,
  TableRowProps,
} from 'react-virtualized';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ReportHeader from './ReportHeader';
import { ReportArea, SC5Report } from '../types/SC5Report';
import NumberFormatter from '../modules/NumberFormatter';
import DateFormatter from '../modules/DateFormatter';
import { CustRReport } from '../types/CustRReport';

export interface ReportPanelProps{
  deviceType: string;
  sc5Report: SC5Report;
  custRReport: CustRReport;
}

interface CombinedArea extends ReportArea{
  custRestored: number;
}

const ReportConfig = {
  fields: [{
    name: 'name',
    label: 'County Name',
  }, {
    name: 'cust_a',
    label: 'Customers Affected',
    icon: 'exclamation-triangle',
  }, {
    name: 'cust_s',
    label: 'Customers Served',
    icon: 'user',
  }],
};

export const ReportPanel = ({ deviceType, sc5Report, custRReport }: ReportPanelProps): JSX.Element => {
  const sc5ReportAreas = sc5Report.areas;
  const reportAreas: CombinedArea[] = sc5ReportAreas.map((sc5ReportArea: ReportArea): CombinedArea => {
    const combinedArea = {
      ...sc5ReportArea,
      custRestored: 0,
    };
    combinedArea.custRestored = custRReport.counties[combinedArea.name];
    return combinedArea;
  });

  const headerRenderer = (
    {
      dataKey,
      label,
      sortBy,
      sortDirection,
    }: TableHeaderProps,
  ): React.ReactNode => {
    const children = [
      <span

        key='label'
        title={typeof label === 'string' ? label : undefined}>
        <span className={'header-icon'}>
        {dataKey === 'cust_a' && deviceType !== 'mobile' && <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} color={'white'}/>}
          {dataKey === 'cust_s' && deviceType !== 'mobile' && <FontAwesomeIcon icon={['fas', 'user']} color={'white'}/>}
          {dataKey === 'custRestored' && deviceType !== 'mobile' && <FontAwesomeIcon icon={['fas', 'calendar-check']} color={'white'}/>}
        </span>
        <div className={`header-label device-${deviceType}`}>{label}</div>
        <span className={'sort-indicator'}>
        {sortBy === dataKey && sortDirection === 'ASC' && <FontAwesomeIcon icon={['fas', 'caret-up']} color={'white'}/>}
        {sortBy === dataKey && sortDirection === 'DESC' && <FontAwesomeIcon icon={['fas', 'caret-down']} color={'white'}/>}
        </span>
    </span>,
    ];

    return children;
  };

  const rowRenderer = (
    {
      className,
      columns,
      index,
      key,
      onRowClick,
      onRowDoubleClick,
      onRowMouseOut,
      onRowMouseOver,
      onRowRightClick,
      rowData,
      style,
    }: TableRowProps,
  ): React.ReactNode => {
    const a11yProps: any = { 'aria-rowindex': index + 1 };

    if (
      onRowClick
      || onRowDoubleClick
      || onRowMouseOut
      || onRowMouseOver
      || onRowRightClick
    ) {
      a11yProps['aria-label'] = 'row';
      a11yProps.tabIndex = 0;

      if (onRowClick) {
        a11yProps.onClick = (event: React.MouseEvent<HTMLInputElement>): void => onRowClick({ event, index, rowData });
      }
      if (onRowDoubleClick) {
        a11yProps.onDoubleClick = (event: React.MouseEvent<HTMLInputElement>): void =>
          onRowDoubleClick({ event, index, rowData });
      }
      if (onRowMouseOut) {
        a11yProps.onMouseOut = (event: React.MouseEvent<HTMLInputElement>): void => onRowMouseOut({
          event,
          index,
          rowData,
        });
      }
      if (onRowMouseOver) {
        a11yProps.onMouseOver = (event: React.MouseEvent<HTMLInputElement>): void => onRowMouseOver({
          event,
          index,
          rowData,
        });
      }
      if (onRowRightClick) {
        a11yProps.onContextMenu = (event: React.MouseEvent<HTMLInputElement>): void =>
          onRowRightClick({ event, index, rowData });
      }
    }

    return (
      <div
        {...a11yProps}
        className={className}
        key={key}
        role='row'
        style={style}>
        {columns}
      </div>
    );
  };

  const _sortList = (list: CombinedArea[], sortBy: string, sortDirection: SortDirectionType): CombinedArea[] => {
    const sorted = list.sort((areaA, areaB): number => {
      if (sortBy === 'cust_a') {
        return areaA.cust_a.val - areaB.cust_a.val;
      }
      return areaA[sortBy] > areaB[sortBy] ? 1 : -1;
    });
    if (sortDirection === 'ASC') {
      return sorted;
    }
    return sorted.reverse();
  };

  const cellRender = ({ cellData }: TableCellProps): JSX.Element => (
    <span className={'table-cell-inner'}><NumberFormatter>{cellData}</NumberFormatter></span>
  );

  const [sortedList, setSortList] = useState(_sortList(reportAreas, 'name', 'ASC'));
  const [sortDirection, setSortDirection] = useState<SortDirectionType>('ASC');
  const [sortBy, setSortBy] = useState('name');
  const _sort = (sortProps: {sortBy: string; sortDirection: SortDirectionType}): void => {
    const sorted = _sortList(sortedList, sortProps.sortBy, sortProps.sortDirection);
    setSortList(sorted);
    setSortDirection(sortProps.sortDirection);
    setSortBy(sortProps.sortBy);
  };

  return (
    <div className={`report-panel device-${deviceType}`}>
      <ReportHeader deviceType={deviceType} custA={sc5Report.totals.cust_a.val} custS={sc5Report.totals.cust_s} custR={custRReport.totalCustR}/>
      <div style={{ flex: '1 1 auto' }}>
        <AutoSizer>
          {({ width, height }): JSX.Element => (
            <Table
              columnCount={2}
              headerHeight={50}
              height={height}
              rowCount={sortedList.length}
              rowGetter={({ index }): ReportArea => sortedList[(index % sortedList.length)]}
              width={width === 0 ? 1 : width}
              rowHeight={50}
              rowStyle={{ alignItems: 'stretch' }}
              headerClassName={'table-header'}
              rowClassName={({ index }): string => `report-row ${index % 2 === 0 ? ' even' : ' odd'}`}
              rowRenderer={rowRenderer}
              sort={_sort}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              <Column width={400}
                      dataKey={ReportConfig.fields[0].name}
                      label={ReportConfig.fields[0].label}
                      flexGrow={1}
                      headerRenderer={headerRenderer}
                      headerClassName={'countyName'}
                      className={'table-cell name'}
                      cellRenderer={cellRender}
              />
              <Column width={400}
                      dataKey={ReportConfig.fields[1].name}
                      label={ReportConfig.fields[1].label}
                      cellDataGetter={({ rowData }): number => rowData.cust_a.val}
                      headerRenderer={headerRenderer}
                      headerClassName={'cust-a'}
                      className={'table-cell'}
                      cellRenderer={cellRender}
              />
              <Column width={400}
                      dataKey={ReportConfig.fields[2].name}
                      label={ReportConfig.fields[2].label}
                      headerRenderer={headerRenderer}
                      headerClassName={'cust-s'}
                      className={'table-cell'}
                      cellRenderer={cellRender}
              />
              <Column width={400}
                      dataKey={'custRestored'}
                      label={'Customers Restored'}
                      headerRenderer={headerRenderer}
                      className={'table-cell'}
                      cellRenderer={cellRender}
              />
            </Table>
          )}
        </AutoSizer>
      </div>
      {deviceType === 'mobile'
      && <div className={`mobile-footer device-${deviceType}`}>
        <div className={`mobile-footer-inner device-${deviceType}`}>
          <div>
            <span>Total Customers Affected &nbsp; <span style={{ fontWeight: 500 }}><NumberFormatter>{sc5Report.totals.cust_a.val}</NumberFormatter></span></span>
          </div>
          <div>
            <span>Total Customers Served &nbsp; <span style={{ fontWeight: 500 }}><NumberFormatter>{sc5Report.totals.cust_s}</NumberFormatter></span></span>
          </div>
          <div>
            <span>Total Customers Restored &nbsp; <span style={{ fontWeight: 500 }}><NumberFormatter>{custRReport.totalCustR}</NumberFormatter></span></span>
          </div>
        </div>
      </div>}
      <div className={`report-footer device-${deviceType}`}>
        <div className={`report-footer-inner device-${deviceType}`}>
          <span style={{ fontWeight: 500 }}>Last Updated: <span><DateFormatter>{sc5Report.updatedAt}</DateFormatter>. &nbsp;</span></span>
          <span>Information is updated approximately every 10 minutes.</span>
        </div>
      </div>
    </div>

  );
};

export default ReportPanel;
